import { createAsyncThunk } from '@reduxjs/toolkit';
import { showToast } from '@/store/notifications/slice';
import chevron from '@/services/chevron';

import { isNetworkError } from '@/utils/helperFunc';
import type { CreateSectionRequest, Section, UpdateSectionRequest } from './types';
import { CURRENT_SCHEMA_VERSION } from '@builder4/shared/migrations/craft/registry';
import { CraftJsonDto } from '@builder4/shared/dto/craft';
import { runCraftJsonMigrations } from '@builder4/shared/migrations/craft';
import { validate } from 'class-validator';
import { formatValidationErrors } from '@builder4/shared/utils/validators';
import { captureSentryException } from '@/utils/sentry';
import type { RootState } from '../store';
import { selectUserAccessId } from '../user/selectors';

// POST → create new section
export const createSection = createAsyncThunk<Section, CreateSectionRequest>(
  'sections/create',
  async (body, { dispatch, getState }) => {
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/sections`;
    try {
      // we assume section data being saved is migrated to the latest version
      body.craft_json.version = CURRENT_SCHEMA_VERSION;

      let response = (await chevron.http({
        url,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      })) as Section;
      const accessId = selectUserAccessId(getState() as RootState);
      const createdAt = response.createdAt ?? new Date().toISOString();
      response = {
        ...response,
        craftJson: body.craft_json,
        sectionGroupId: body.section_group_id,
        name: body.name,
        // Backfill fields the create endpoint omits so the new section renders immediately.
        ownerId: accessId ?? response.ownerId,
        createdAt,
        updatedAt: response.updatedAt ?? createdAt,
        deleted: response.deleted ?? false,
        published: response.published ?? false,
      };
      // ✅ Only show success toast if explicitly requested
      if (body.showDuplicateSuccessToast) {
        dispatch(showToast({ text: 'Section duplicated successfully!', state: 'success' }));
      }

      return response;
    } catch (error) {
      const isNetworkErr = isNetworkError(error);

      if (isNetworkErr) {
        // For network errors, show a toast but the global error page will also show
        dispatch(showToast({ text: 'Cannot create section. Check connection.', state: 'warning' }));
      } else {
        // For non-network errors, show error toast
        dispatch(
          showToast({ text: 'Failed to create new section. Please try again.', state: 'error' }),
        );
      }
      throw error;
    }
  },
);

// GET → fetch all sections
export const fetchSections = createAsyncThunk<Section[]>(
  'sections/getAll',
  async (_, { dispatch }) => {
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/sections`;
    try {
      const response = (await chevron.http({
        url,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })) as Section[];

      // Migrate all sections to latest version and filter out anything invalid
      for (let i = response.length - 1; i >= 0; i--) {
        const section = response[i];
        const dto = CraftJsonDto.fromSection(section.craftJson);
        const migrationResult = runCraftJsonMigrations(dto);
        if (migrationResult.error) {
          const error = new Error(`Section migration error: ${migrationResult.errorMessage}`);
          captureSentryException(error, {
            tags: { feature: 'section-loading', operation: 'migration' },
            extra: { sectionId: section.id, errorMessage: migrationResult.errorMessage },
            level: 'error',
          });
          console.error('Migration error: ', migrationResult.errorMessage);
          response.splice(i, 1);
          continue;
        }
        const migratedCraftJson = migrationResult.json;
        const validationResult = await validate(migratedCraftJson);

        if (validationResult.length > 0) {
          const validationErrors = formatValidationErrors(validationResult);
          const error = new Error('Invalid section craftJson');
          captureSentryException(error, {
            tags: { feature: 'section-loading', operation: 'validation' },
            extra: { sectionId: section.id, validationErrors },
            level: 'error',
          });
          console.error(`Invalid section json for ${section.id}: `, validationErrors);
          response.splice(i, 1);
          continue;
        }
        section.craftJson = migratedCraftJson.toSectionFormat();
      }

      return response;
    } catch (error) {
      const isNetworkErr = isNetworkError(error);

      if (isNetworkErr) {
        // For network errors, show a toast but the global error page will also show
        dispatch(showToast({ text: 'Cannot reload. Check connection.', state: 'warning' }));
      } else {
        // For non-network errors, show error toast
        dispatch(showToast({ text: 'Error fetching sections. Please try again', state: 'error' }));
      }
      throw error;
    }
  },
);

// PATCH → update section by id
export const updateSectionById = createAsyncThunk<
  Section,
  { sectionId: string; body: UpdateSectionRequest }
>('sections/updateSectionById', async ({ sectionId, body }, { dispatch }) => {
  const url = `${import.meta.env.VITE_API_STARGATE}/builder4/sections/${sectionId}`;
  try {
    // we assume section data being saved is migrated to the latest version
    const craftJsonWithVersion = {
      ...body.craftJson,
      version: CURRENT_SCHEMA_VERSION,
    } as UpdateSectionRequest['craftJson'];

    let response = (await chevron.http({
      url,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: {
        ...body,
        craftJson: craftJsonWithVersion,
      },
    })) as Section;

    response = {
      ...response,
      name: body.name,
      craftJson: craftJsonWithVersion,
      sectionGroupId: body.sectionGroupId,
    };
    const toastText =
      body.published === true
        ? 'Section published successfully'
        : body.published === false
          ? 'Section unpublished successfully'
          : 'Changes saved successfully';
    dispatch(showToast({ text: toastText, state: 'success' }));

    return response;
  } catch (error) {
    const isNetworkErr = isNetworkError(error);

    if (isNetworkErr) {
      // For network errors, show a toast but the global error page will also show
      dispatch(showToast({ text: 'Cannot save. Check connection.', state: 'warning' }));
    } else {
      // For non-network errors, show error toast
      dispatch(showToast({ text: 'Save failed. Please try again.', state: 'error' }));
    }
    throw error;
  }
});

// Delete → section by id
export const deleteSectionById = createAsyncThunk<{ id: string }, string>(
  'sections/deleteSectionById',
  async (sectionId, { dispatch }) => {
    const url = `${import.meta.env.VITE_API_STARGATE}/builder4/sections/${sectionId}`;
    try {
      let response = (await chevron.http({
        url,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })) as { id: string };
      response = {
        id: sectionId,
      };
      dispatch(
        showToast({
          text: 'Section deleted',
          state: 'success',
        }),
      );

      return response;
    } catch (error) {
      const isNetworkErr = isNetworkError(error);

      if (isNetworkErr) {
        // For network errors, show a toast but the global error page will also show
        dispatch(showToast({ text: 'Cannot delete. Check connection.', state: 'warning' }));
      } else {
        // For non-network errors, show error toast
        dispatch(showToast({ text: 'Cannot delete. Please try again.', state: 'error' }));
      }
      throw error;
    }
  },
);
